import { getReportPostList } from "apis";
import { deleteReportPost, getAdminReportList, resolveAdminReport } from "apis/admin.api";
import { Post } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";

const TARGET_TYPE_LABELS: Record<string, string> = {
    service: '🛒 서비스',
    user: '👤 사용자',
    post: '📝 게시글',
};

const ReportManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [activeTab, setActiveTab] = useState<'universal' | 'post'>('universal');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const itemsPerPage = 10;

    // ── Universal Reports (서비스/유저/게시글 통합 신고) ──
    const { data: universalData, isLoading: uLoading } = useQuery(
        ["universalReportList", currentPage, itemsPerPage],
        () => getAdminReportList(currentPage, itemsPerPage),
        { keepPreviousData: true, enabled: activeTab === 'universal' }
    );

    // ── Post Reports (기존 게시글 신고) ──
    const { data: postData, isLoading: pLoading } = useQuery(
        ["reportList", currentPage, itemsPerPage],
        () => getReportPostList(currentPage, itemsPerPage),
        { keepPreviousData: true, enabled: activeTab === 'post' }
    );

    const handleTabChange = (tab: 'universal' | 'post') => {
        setActiveTab(tab);
        setCurrentPage(1);
        setExpandedRow(null);
    };

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);
    const toggleRow = (id: string) => setExpandedRow(expandedRow === id ? null : id);

    // ── Post report handlers ──
    const handleDeleteClick = async (reportId: string) => {
        if (!(await showConfirm('해당 게시글을 삭제하시겠습니까?', { title: '게시글 삭제', confirmText: '삭제', variant: 'warning' }))) return;
        try {
            const response = await deleteReportPost(reportId);
            if (response.status === 200) {
                showAlert("게시글이 삭제되었습니다.", { variant: 'success' });
                queryClient.invalidateQueries(["reportList"]);
            }
        } catch (error) { console.error(error); }
    };

    // ── Universal report handlers ──
    const handleResolve = async (reportId: string) => {
        if (!(await showConfirm('이 신고를 처리 완료 하시겠습니까?', { title: '신고 처리', confirmText: '완료', variant: 'warning' }))) return;
        try {
            await resolveAdminReport(reportId);
            showAlert("처리 완료되었습니다.", { variant: 'success' });
            queryClient.invalidateQueries(["universalReportList"]);
        } catch (error) { console.error(error); }
    };

    const postReportReasons = (item: any) => {
        const reasons = [];
        if (item.illegalChecked) reasons.push('불법 콘텐츠');
        if (item.inappropriateChecked) reasons.push('부적절한 콘텐츠');
        if (item.fraudChecked) reasons.push('사기 의심');
        if (item.copyrightChecked) reasons.push('저작권 침해');
        if (item.spamChecked) reasons.push('스팸');
        return reasons;
    };

    const universalReports = universalData?.data?.data?.items || [];
    const universalTotal = universalData?.data?.data?.total || 0;
    const postReports = postData?.data?.data?.items || [];
    const postTotal = postData?.data?.data?.total || 0;

    const isLoading = activeTab === 'universal' ? uLoading : pLoading;
    const totalItems = activeTab === 'universal' ? universalTotal : postTotal;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">신고 관리</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}건의 신고</p>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => handleTabChange('universal')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activeTab === 'universal'
                            ? 'bg-red-500 text-white'
                            : 'bg-achromatic-100 text-achromatic-500 hover:bg-achromatic-200'
                    }`}
                >
                    🚨 통합 신고 ({universalTotal})
                </button>
                <button
                    onClick={() => handleTabChange('post')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activeTab === 'post'
                            ? 'bg-red-500 text-white'
                            : 'bg-achromatic-100 text-achromatic-500 hover:bg-achromatic-200'
                    }`}
                >
                    📝 게시글 신고 ({postTotal})
                </button>
            </div>

            {isLoading ? (
                <div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" />
            ) : (
                <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden">
                    {activeTab === 'universal' ? (
                        /* ── Universal Reports Table ── */
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-achromatic-100 bg-achromatic-50">
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">#</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">유형</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">신고자</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">대상</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">피신고인</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">사유</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-center">날짜</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-center">상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                {universalReports.map((item: any, index: number) => (
                                    <>
                                        <tr
                                            key={item.id}
                                            className={`border-b border-achromatic-50 cursor-pointer transition-colors hover:bg-primary-50/30
                                                ${expandedRow === item.id ? 'bg-primary-50/20' : ''}`}
                                            onClick={() => toggleRow(item.id)}
                                        >
                                            <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                            <td className="py-3 px-4 text-[12px]">
                                                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                                                    item.targetType === 'service' ? 'bg-blue-50 text-blue-600'
                                                    : item.targetType === 'user' ? 'bg-purple-50 text-purple-600'
                                                    : 'bg-amber-50 text-amber-600'
                                                }`}>
                                                    {TARGET_TYPE_LABELS[item.targetType] || item.targetType}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-[13px] font-medium text-achromatic-800">
                                                {item.reporter?.name || '-'}
                                            </td>
                                            <td className="py-3 px-4 text-[13px] text-achromatic-600 truncate max-w-[200px]">
                                                {item.targetName || item.targetId?.slice(0, 8)}
                                            </td>
                                            <td className="py-3 px-4 text-[13px] text-achromatic-600">
                                                {item.targetOwnerName || '-'}
                                            </td>
                                            <td className="py-3 px-4 text-[13px] text-red-500 font-medium">
                                                {item.reasonLabel || item.reason}
                                            </td>
                                            <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                                {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                                                    ${item.isResolved ? 'bg-achromatic-100 text-achromatic-500' : 'bg-red-50 text-red-600'}`}>
                                                    {item.isResolved ? '처리됨' : '대기'}
                                                </span>
                                            </td>
                                        </tr>

                                        {expandedRow === item.id && (
                                            <tr key={`expanded-${item.id}`} className="bg-achromatic-50/50">
                                                <td colSpan={8} className="p-6">
                                                    <div className="grid grid-cols-2 gap-6 mb-4">
                                                        <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                            <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">신고 상세</h3>
                                                            <div className="flex flex-col gap-2 text-[13px]">
                                                                <div><span className="text-achromatic-400">유형:</span> <span className="font-medium">{TARGET_TYPE_LABELS[item.targetType]}</span></div>
                                                                <div><span className="text-achromatic-400">대상 이름:</span> <span className="font-medium">{item.targetName || '-'}</span></div>
                                                                <div><span className="text-achromatic-400">대상 ID:</span> <span className="font-mono text-[11px]">{item.targetId}</span></div>
                                                                <div><span className="text-achromatic-400">피신고인:</span> <span className="font-medium">{item.targetOwnerName || '-'}</span></div>
                                                                <div><span className="text-achromatic-400">피신고인 ID:</span> <span className="font-mono text-[11px]">{item.targetOwnerId || '-'}</span></div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                            <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">사유 및 상세</h3>
                                                            <div className="flex flex-col gap-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="w-5 h-5 bg-red-50 rounded-full flex items-center justify-center text-[10px] text-red-500">⚠</span>
                                                                    <span className="text-[13px] font-medium text-red-600">{item.reasonLabel || item.reason}</span>
                                                                </div>
                                                                {item.detail && (
                                                                    <div className="mt-2 p-3 bg-achromatic-50 rounded-lg text-[13px] text-achromatic-700">
                                                                        {item.detail}
                                                                    </div>
                                                                )}
                                                                {!item.detail && (
                                                                    <span className="text-[13px] text-achromatic-400 mt-1">추가 상세 없음</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end">
                                                        {!item.isResolved && (
                                                            <button
                                                                className="px-5 py-2 bg-green-500 text-white text-[12px] font-bold rounded-lg hover:bg-green-600 transition-colors"
                                                                onClick={(e) => { e.stopPropagation(); handleResolve(item.id); }}
                                                            >
                                                                ✅ 처리 완료
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                ))}
                                {universalReports.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="py-16 text-center text-achromatic-300">
                                            신고 내역이 없습니다
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        /* ── Post Reports Table (기존) ── */
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-achromatic-100 bg-achromatic-50">
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">#</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">신고자</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-left">게시글 내용</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-center">신고일</th>
                                    <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 text-center">상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                {postReports.map((item: any, index: number) => (
                                    <>
                                        <tr
                                            key={item.id}
                                            className={`border-b border-achromatic-50 cursor-pointer transition-colors hover:bg-primary-50/30
                                                ${expandedRow === item.id ? 'bg-primary-50/20' : ''}`}
                                            onClick={() => toggleRow(item.id)}
                                        >
                                            <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                            <td className="py-3 px-4 text-[13px] font-medium text-achromatic-800">{item.reporter?.name}</td>
                                            <td className="py-3 px-4 text-[13px] text-achromatic-500 truncate max-w-[250px]">{item.reportedPost?.content}</td>
                                            <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                                {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                                                    ${item.reportedPost?.isDeleted === 1 ? 'bg-achromatic-100 text-achromatic-500' : 'bg-red-50 text-red-600'}`}>
                                                    {item.reportedPost?.isDeleted === 1 ? '삭제됨' : '대기'}
                                                </span>
                                            </td>
                                        </tr>
                                        {expandedRow === item.id && (
                                            <tr key={`expanded-${item.id}`} className="bg-achromatic-50/50">
                                                <td colSpan={5} className="p-6">
                                                    <div className="grid grid-cols-2 gap-6 mb-4">
                                                        <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                            <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">신고 게시글</h3>
                                                            <div className="max-h-[300px] overflow-y-auto">
                                                                <Post post={item.reportedPost} />
                                                            </div>
                                                        </div>
                                                        <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                            <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">신고 사유</h3>
                                                            <div className="flex flex-col gap-2">
                                                                {postReportReasons(item).map(reason => (
                                                                    <div key={reason} className="flex items-center gap-2">
                                                                        <span className="w-5 h-5 bg-red-50 rounded-full flex items-center justify-center text-[10px] text-red-500">⚠</span>
                                                                        <span className="text-[13px] text-achromatic-700">{reason}</span>
                                                                    </div>
                                                                ))}
                                                                {postReportReasons(item).length === 0 && (
                                                                    <span className="text-[13px] text-achromatic-400">사유 없음</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end">
                                                        {item.reportedPost?.isDeleted !== 1 && (
                                                            <button
                                                                className="px-5 py-2 bg-red-500 text-white text-[12px] font-bold rounded-lg hover:bg-red-600 transition-colors"
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id); }}
                                                            >
                                                                게시글 삭제
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className="p-4 border-t border-achromatic-100 flex justify-center">
                        <ReactPaginate
                            previousLabel={"←"} nextLabel={"→"} breakLabel={"..."} pageCount={pageCount}
                            marginPagesDisplayed={1} pageRangeDisplayed={5} onPageChange={handlePageClick}
                            containerClassName={"flex items-center gap-1"}
                            previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                            nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                            pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100"}
                            activeClassName={"!bg-primary-500 !text-white"} activeLinkClassName={"text-white"}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportManageComponent;